/**
 * 발사체 벽 반사 경계 + 반사 계산 (순수 — Phaser 비의존).
 *
 * 충돌 경계의 SSoT는 게임뷰 프레임(GameScene.gameviewRect의 interior)이다.
 * 기존엔 Projectile.applyBounce가 화면 전체 폭(scale.width)·COMBAT.playTop을 따로 써서
 * 게임뷰 프레임보다 바깥에서 반사 → 공이 프레임 테두리 밖으로 빠져나가 보였다.
 * 이 모듈로 경계를 값(PlayfieldBounds)으로 주입받아 프레임 안에서만 튕기게 한다.
 */

/** 발사체가 갇히는 사각형 경계 (게임뷰 interior 픽셀 좌표). */
export interface PlayfieldBounds {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

/** 반사 후 속도. */
export interface ReflectedVelocity {
  readonly vx: number;
  readonly vy: number;
}

/**
 * 반지름 r 원이 bounds 안에서 튕길 때의 반사 후 속도.
 * 경계를 벗어나려는 축(가장자리가 경계에 닿았고 그쪽으로 진행 중)만 부호 반전한다.
 * 이미 안쪽으로 향하는 속도는 반전하지 않아 경계에 붙은 채 떨리는 재반사를 막는다.
 */
export function reflectInBounds(
  x: number,
  y: number,
  vx: number,
  vy: number,
  bounds: PlayfieldBounds,
  radius: number,
): ReflectedVelocity {
  let nvx = vx;
  let nvy = vy;
  if (x <= bounds.left + radius && vx < 0) nvx = -vx;
  else if (x >= bounds.right - radius && vx > 0) nvx = -vx;
  if (y <= bounds.top + radius && vy < 0) nvy = -vy;
  else if (y >= bounds.bottom - radius && vy > 0) nvy = -vy;
  return { vx: nvx, vy: nvy };
}

/**
 * 벽돌 충돌 시 발사체가 튕겨나갈 단위 방향(현재 속도의 반대 방향). 부수효과 없음 — 속도를 변경하지 않는다.
 *
 * 이 방향은 비-관통 발사체의 벽돌 흔들림(playImpactShake)·소멸 fade tween 방향에만 쓰인다.
 * 관통(pierce) 발사체는 이 방향을 무시하고 진행 속도를 유지해야 하므로, 방향 계산과 속도 적용을
 * 분리했다 — 예전엔 충돌 즉시 body.setVelocity로 속도를 반전해 관통 공도 튕겨나갔다(회귀 버그).
 * 속도가 0이면(정지 상태 충돌) 발사체→벽돌의 반대 방향으로 폴백한다.
 */
export function reboundDirection(
  vx: number,
  vy: number,
  projX: number,
  projY: number,
  brickX: number,
  brickY: number,
): { x: number; y: number } {
  let dvx = vx;
  let dvy = vy;
  let speed = Math.hypot(vx, vy);
  if (speed <= 0) {
    dvx = brickX - projX;
    dvy = brickY - projY;
    speed = Math.hypot(dvx, dvy);
  }
  // -0 정규화(x || 0) — 부호 있는 0은 방향으로서 의미가 없고 toEqual/JSON 직렬화에서 0과 다르게 취급된다.
  const x = speed > 0 ? -dvx / speed : 0;
  const y = speed > 0 ? -dvy / speed : -1;
  return { x: x || 0, y: y || 0 };
}

/**
 * 벽돌(사각형) 충돌 시 발사체 속도를 면 기준으로 반사한다 — 벽 반사(reflectInBounds)와 같은 법선 반사.
 * 발사체-벽돌 중심 상대 위치를 벽돌 반치수로 정규화해 침투가 큰 축(닿은 면)을 판정하고, 그 축 속도만
 * 바깥 방향으로 반전한다(다른 축은 보존). reboundDirection(속도 정반대 = 180도 역행)과 달리 횡 성분이
 * 유지돼 "왔던 길로 되돌아가는" 부자연스러운 반사를 막는다. 분열 공의 첫 충돌 반사(reflectOffBrick)에 쓰인다.
 */
export function reflectVelocityOffRect(
  vx: number,
  vy: number,
  projX: number,
  projY: number,
  rectX: number,
  rectY: number,
  halfW: number,
  halfH: number,
): ReflectedVelocity {
  const dx = projX - rectX;
  const dy = projY - rectY;
  // 반치수로 정규화한 침투 비율 — 큰 쪽이 닿은 면(좌우 vs 상하). halfW/halfH=0 방어.
  const ox = halfW > 0 ? Math.abs(dx) / halfW : Math.abs(dx);
  const oy = halfH > 0 ? Math.abs(dy) / halfH : Math.abs(dy);
  if (ox >= oy) {
    // 좌/우 면 — vx를 발사체가 있는 바깥 방향(dx 부호)으로, vy는 보존.
    const mag = Math.abs(vx);
    return { vx: dx >= 0 ? mag : -mag, vy };
  }
  // 상/하 면 — vy를 바깥 방향(dy 부호)으로, vx는 보존.
  const mag = Math.abs(vy);
  return { vx, vy: dy >= 0 ? mag : -mag };
}

/**
 * 반사 모듈이 없는(또는 반사 횟수를 소진한) 공이 소멸해야 하는가 — 좌/우/상 프레임 벽에 닿으면 true.
 * 하단은 방향(상승/하강)을 따져야 하므로 여기서 제외하고 fallsBelowFloor가 별도로 판정한다 —
 * 발사구(런처가 프레임 하단 경계 밖)에서 위로 나오는 발사 직후 공을 즉시 소멸시키지 않기 위함.
 */
export function reachesWall(x: number, y: number, bounds: PlayfieldBounds, radius: number): boolean {
  return (
    x <= bounds.left + radius || x >= bounds.right - radius || y <= bounds.top + radius
  );
}

/**
 * 반사 못 하는 공이 바닥(하단 경계)으로 내려와 소멸해야 하는가 — 하단 경계 도달 + 하강 중(vy>0)이면 true.
 * 발사구가 하단 경계 밖이라 발사 직후 위로 솟는 공(vy<0)은 통과시키고, 되돌아 떨어지는 공만 소멸시킨다.
 * ⚠ 경계식(y >= bottom - radius && vy > 0)은 reflectInBounds 하단 반사 분기와 동일 — 한쪽 수정 시 함께 맞출 것.
 */
export function fallsBelowFloor(
  y: number,
  vy: number,
  bounds: PlayfieldBounds,
  radius: number,
): boolean {
  return y >= bounds.bottom - radius && vy > 0;
}
